/**
 * Storage Utilities für das Rating Widget
 * Verwaltet die Speicherung und Abfrage von Bewertungen im localStorage
 */
import { STORAGE_KEY } from './constants.js'

/**
 * Prüft, ob localStorage im Browser verfügbar ist
 * @returns {boolean} - True wenn localStorage verfügbar ist
 */
export function isLocalStorageAvailable() {
    try {
        localStorage.setItem('test', 'test')
        const value = localStorage.getItem('test')
        localStorage.removeItem('test')
        return value === 'test'
    } catch (e) {
        return false
    }
}

/**
 * Gibt die aktuelle Seiten-URL ohne Domain zurück
 * @returns {string} - Die URL ohne Domain (z.B. /path/to/page)
 */
export function getCurrentPagePath() {
    return window.location.pathname + window.location.search;
}

/**
 * Ruft eine gespeicherte Bewertung ab
 * @param {string} pid - Die PID der Seite
 * @returns {Object|null} - Die gespeicherte Bewertung oder null
 */
export function getRatingFromStorage(pid) {
    try {
        if (!isLocalStorageAvailable()) return null

        const storedRatings = localStorage.getItem(STORAGE_KEY)
        if (!storedRatings) return null

        const ratings = JSON.parse(storedRatings)
        return ratings[pid] || null
    } catch (error) {
        return null
    }
}

/**
 * Prüft, ob die aktuelle Seite bereits bewertet wurde
 * @param {string} pid - Die PID der Seite
 * @returns {boolean} - True wenn die aktuelle Seite bereits bewertet wurde
 */
export function isCurrentPageRated(pid) {
    try {
        if (!isLocalStorageAvailable()) {
            return false;
        }

        const storedRatings = localStorage.getItem(STORAGE_KEY);
        if (!storedRatings) {
            return false;
        }

        const ratings = JSON.parse(storedRatings);
        
        if (!ratings[pid]) {
            return false;
        }

        // Aktuelle URL (ohne Domain) abrufen
        const currentPath = getCurrentPagePath();
        
        // Prüfen ob die aktuelle URL bereits bewertet wurde
        const isRated = ratings[pid].urls && ratings[pid].urls.includes(currentPath);
        
        return isRated;
    } catch (error) {
        return false;
    }
}

/**
 * Speichert eine Bewertung im localStorage
 * @param {string} pid - Die PID der Seite
 * @param {number} rating - Die Bewertung (1-5)
 * @returns {boolean} - True bei Erfolg
 */
export function saveRatingToStorage(pid, rating) {
    try {
        if (!isLocalStorageAvailable()) return false
        if (!pid || typeof rating !== 'number' || isNaN(rating)) {
            return false
        }

        let ratings = {}
        const storedRatings = localStorage.getItem(STORAGE_KEY)

        if (storedRatings) {
            try {
                ratings = JSON.parse(storedRatings)
            } catch (e) {
                ratings = {}
            }
        }

        // Aktuelle URL (ohne Domain) abrufen
        const currentPath = getCurrentPagePath();

        // Bestehenden Eintrag für PID prüfen und aktualisieren
        if (ratings[pid]) {
            // Bewertung und Timestamp aktualisieren
            ratings[pid].rating = rating;
            ratings[pid].timestamp = Date.now();
            
            // URLs-Array initialisieren, falls nicht vorhanden
            if (!ratings[pid].urls) {
                ratings[pid].urls = [];
            }
            
            // URL hinzufügen, falls nicht bereits vorhanden
            if (!ratings[pid].urls.includes(currentPath)) {
                ratings[pid].urls.push(currentPath);
            }
        } else {
            // Neuen Eintrag erstellen
            ratings[pid] = {
                rating: rating,
                timestamp: Date.now(),
                urls: [currentPath]
            }
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(ratings))

        // Verifiziere die Speicherung
        const verifyStored = localStorage.getItem(STORAGE_KEY)
        if (!verifyStored) {
            return false
        }

        return true
    } catch (error) {
        return false
    }
}
